import { ElementType, Suspense, lazy } from 'react';
// components
import LoadingScreen from 'src/components/loading-screen';
import VoiceReceptionistDashboardSkeleton from 'src/sections/@dashboard/voice-receptionist/components/VoiceReceptionistDashboardSkeleton';

// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: any) =>
  (
    <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );

// ----------------------------------------------------------------------

// AUTH
export const LoginPage = Loadable(
  lazy(() => import('../pages/auth/LoginPage')),
);
export const PhoneLoginPage = Loadable(
  lazy(() => import('../pages/auth/PhoneLoginPage')),
);
export const RegisterPage = Loadable(
  lazy(() => import('../pages/auth/RegisterPage')),
);
export const ProfessionalRegisterPage = Loadable(
  lazy(() => import('../pages/auth/ProfessionalRegisterPage')),
);
export const ProfessionalLoginPage = Loadable(
  lazy(() => import('../pages/auth/ProfessionalLoginPage')),
);
export const OrganizationSetupPage = Loadable(
  lazy(() => import('../pages/auth/OrganizationSetupPage')),
);
export const ConfirmationPage = Loadable(
  lazy(() => import('../pages/auth/ConfirmationPage')),
);
export const VerificationPage = Loadable(
  lazy(() => import('../pages/auth/VerificationPage')),
);
export const NewPasswordPage = Loadable(
  lazy(() => import('../pages/auth/NewPasswordPage')),
);
export const ResetPasswordPage = Loadable(
  lazy(() => import('../pages/auth/ResetPasswordPage')),
);
export const ResetPasswordRequestSentPage = Loadable(
  lazy(() => import('../pages/auth/ResetPasswordRequestSentPage')),
);
export const AcceptTermsPage = Loadable(
  lazy(() => import('../pages/auth/AcceptTermsPage')),
);
export const AccountClosedPage = Loadable(
  lazy(() => import('../pages/auth/AccountClosedPage')),
);
export const TermsConditionsPage = Loadable(
  lazy(() => import('../pages/auth/TermsConditionsPage')),
);
export const PrivacyPolicyPage = Loadable(
  lazy(() => import('../pages/auth/PrivacyPolicyPage')),
);
export const PendingDeletionPage = Loadable(
  lazy(() => import('../pages/auth/PendingDeletionPage')),
);

// BOOKING
export const ProfessionalBookingPage = Loadable(
  lazy(() => import('../pages/ProfessionalBookingPage')),
);
// Eventually make public
export const ProfessionalBookingRouter = Loadable(
  lazy(() => import('../pages/booking/ProfessionalBookingRouter')),
);

// DASHBOARD: GENERAL
const loadWaitingRoomPage = () => import('../pages/dashboard/WaitingRoomPage');
export const WaitingRoomPage = Loadable(lazy(loadWaitingRoomPage));
export const ProfilesPage = Loadable(
  lazy(() => import('../pages/dashboard/ProfilesPage')),
);
export const ProductsPage = Loadable(
  lazy(() => import('../pages/dashboard/ProductsPage')),
);
export const ProductDetailsPage = Loadable(
  lazy(() => import('../pages/dashboard/ProductDetailsPage')),
);
export const InsurancePage = Loadable(
  lazy(() => import('../pages/dashboard/InsurancePage')),
);
export const UpcomingVisitsPage = Loadable(
  lazy(() => import('../pages/dashboard/UpcomingVisitsPage')),
);
export const ProfilesVisitsHistoryPage = Loadable(
  lazy(() => import('../pages/dashboard/ProfilesVisitsHistoryPage')),
);
export const ProfilePage = Loadable(
  lazy(() => import('../pages/dashboard/ProfilePage')),
);
export const ProfileTabsVisitsHistoryPage = Loadable(
  lazy(() => import('../pages/dashboard/ProfileTabsVisitsHistoryPage')),
);
export const ProfileTabsFilesPage = Loadable(
  lazy(() => import('../pages/dashboard/ProfileTabsFilesPage')),
);
export const ProfileTabsVisitPage = Loadable(
  lazy(() => import('../pages/dashboard/ProfileTabsVisitPage')),
);
export const VisitNotesPage = Loadable(
  lazy(() => import('../pages/dashboard/VisitNotesPage')),
);
export const VisitPage = Loadable(
  lazy(() => import('../pages/dashboard/VisitPage')),
);
export const ProfileProductsPage = Loadable(
  lazy(() => import('../pages/dashboard/ProfileProductsPage')),
);
export const BookingPage = Loadable(
  lazy(() => import('../pages/dashboard/BookingPage')),
);
export const BookPage = Loadable(
  lazy(() => import('../pages/dashboard/BookPage')),
);
export const FirstBookingPage = Loadable(
  lazy(() => import('../pages/dashboard/FirstBookingPage')),
);
export const LiveVisitPage = Loadable(
  lazy(() => import('../pages/dashboard/LiveVisitPage')),
);
export const BookingCompletedPage = Loadable(
  lazy(() => import('../pages/dashboard/BookingCompletedPage')),
);
export const VisitsHistoryPage = Loadable(
  lazy(() => import('../pages/dashboard/VisitsHistoryPage')),
);
export const StatisticsPage = Loadable(
  lazy(() => import('../pages/dashboard/StatisticsPage')),
);
const loadCalendarPage = () => import('../pages/dashboard/CalendarPage');
export const CalendarPage = Loadable(lazy(loadCalendarPage));

/** Fetches the staff landing pages ahead of a redirect, so it shows no extra loader. */
export const preloadStaffLandingPages = (): Promise<unknown> =>
  Promise.all([loadCalendarPage(), loadWaitingRoomPage()]);
const LazyVoiceReceptionistDashboardPage = lazy(
  () =>
    import(
      'src/pages/dashboard/voice-receptionist/VoiceReceptionistDashboardPage'
    ),
);
export const VoiceReceptionistDashboardPage = (): JSX.Element => (
  <Suspense fallback={<VoiceReceptionistDashboardSkeleton />}>
    <LazyVoiceReceptionistDashboardPage />
  </Suspense>
);
export const VoiceReceptionistCallPage = Loadable(
  lazy(
    () =>
      import(
        'src/pages/dashboard/voice-receptionist/VoiceReceptionistCallPage'
      ),
  ),
);
export const VoicePrivacyNoticePage = Loadable(
  lazy(() => import('src/pages/voice-privacy/VoicePrivacyNoticePage')),
);
export const VoiceDevelopOperatorTrialNoticePage = Loadable(
  lazy(
    () => import('src/pages/voice-privacy/VoiceDevelopOperatorTrialNoticePage'),
  ),
);
export const VoicePrivacyRequestPage = Loadable(
  lazy(() => import('src/pages/voice-privacy/VoicePrivacyRequestPage')),
);

// WEBINARS
export const WebinarPresenterPage = Loadable(
  lazy(() => import('src/pages/webinar/WebinarPresenterPage')),
);
export const WebinarAdminRedirectPage = Loadable(
  lazy(() => import('src/pages/dashboard/webinar/WebinarAdminRedirectPage')),
);

export const OrganizationVerificationPage = Loadable(
  lazy(() => import('../pages/dashboard/OrganizationVerificationPage')),
);
export const UpdateAccountPage = Loadable(
  lazy(() => import('../pages/dashboard/UpdateAccountPage')),
);
export const CouponCodesPage = Loadable(
  lazy(() => import('../pages/dashboard/CouponCodesPage')),
);

// DASHBOARD: USER;
export const AccountPage = Loadable(
  lazy(() => import('../pages/dashboard/AccountPage')),
);
export const AccountTabsGeneralPage = Loadable(
  lazy(() => import('../pages/dashboard/AccountTabsGeneralPage')),
);
export const AccountTabsPasswordPage = Loadable(
  lazy(() => import('../pages/dashboard/AccountTabsPasswordPage')),
);

// SEARCH
export const SearchResultsPage = Loadable(
  lazy(() => import('../pages/search/SearchResultsPage')),
);
export const SearchResultsDetailsPage = Loadable(
  lazy(() => import('../pages/search/SearchResultsDetailsPage')),
);
export const SearchBookingPage = Loadable(
  lazy(() => import('../pages/search/SearchBookingPage')),
);

// PETSY
export const PetsyBookingPage = Loadable(
  lazy(() => import('../pages/dashboard/petsy/PetsyBookingPage')),
);
export const PetsyBookingCompletedPage = Loadable(
  lazy(() => import('../pages/dashboard/petsy/PetsyBookingCompletedPage')),
);

// TEST RENDER PAGE BY ROLE
export const PermissionDeniedPage = Loadable(
  lazy(() => import('../pages/dashboard/PermissionDeniedPage')),
);

// ORGANIZATION
export const OrganizationDetailsPage = Loadable(
  lazy(() => import('../pages/dashboard/organization/OrganizationDetailsPage')),
);
export const OrganizationProfessionalsListPage = Loadable(
  lazy(
    () =>
      import(
        '../pages/dashboard/organization/OrganizationProfessionalsListPage'
      ),
  ),
);
export const OrganizationPatientsListPage = Loadable(
  lazy(
    () =>
      import('../pages/dashboard/organization/OrganizationPatientsListPage'),
  ),
);
export const OrganizationPatientPage = Loadable(
  lazy(() => import('../pages/dashboard/organization/OrganizationPatientPage')),
);

export const OrganizationProfessionalPage = Loadable(
  lazy(
    () =>
      import('../pages/dashboard/organization/OrganizationProfessionalPage'),
  ),
);

export const OrganizationStatisticsPage = Loadable(
  lazy(
    () => import('../pages/dashboard/organization/OrganizationStatisticsPage'),
  ),
);

export const TreatmentsOverviewPage = Loadable(
  lazy(() => import('../pages/dashboard/organization/TreatmentsOverviewPage')),
);

// WAREHOUSE
export const WarehousePage = Loadable(
  lazy(() => import('../pages/dashboard/warehouse/WarehousePage')),
);
export const WarehouseProductsTab = Loadable(
  lazy(
    () =>
      import(
        '../sections/@dashboard/warehouse/components/products/WarehouseProductsContent'
      ),
  ),
);
export const WarehouseDocumentsTab = Loadable(
  lazy(
    () =>
      import(
        '../sections/@dashboard/warehouse/components/documents/WarehouseDocumentsContent'
      ),
  ),
);
export const WarehouseDemandTab = Loadable(
  lazy(
    () =>
      import(
        '../sections/@dashboard/warehouse/components/demand/WarehouseDemandRequestsContent'
      ),
  ),
);
export const WarehouseSalesTab = Loadable(
  lazy(
    () =>
      import(
        '../sections/@dashboard/warehouse/components/sales/WarehouseSalesContent'
      ),
  ),
);
export const WarehouseSuppliersTab = Loadable(
  lazy(
    () =>
      import(
        '../sections/@dashboard/warehouse/components/suppliers/WarehouseSuppliersContent'
      ),
  ),
);

// NOTIFICATIONS
export const NotificationsPage = Loadable(
  lazy(() => import('../pages/dashboard/notifications/NotificationsPage')),
);

// GUEST VISIT
export const GuestVisitEntry = Loadable(
  lazy(() => import('../auth/GuestVisitGuard')),
);
export const GuestManageLinkEntry = Loadable(
  lazy(() => import('../pages/guest/GuestManageLinkPage')),
);
export const GuestBookingContactConfirmationEntry = Loadable(
  lazy(() => import('../pages/guest/GuestBookingContactConfirmationPage')),
);
export const GuestLiveVisitEntry = Loadable(
  lazy(() => import('../pages/guest/GuestLiveVisitPage')),
);

// MAIN
export const Page500 = Loadable(lazy(() => import('../pages/Page500')));
export const Page403 = Loadable(lazy(() => import('../pages/Page403')));
export const Page404 = Loadable(lazy(() => import('../pages/Page404')));
export const MaintenancePage = Loadable(
  lazy(() => import('../pages/MaintenancePage')),
);
